import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@arcadeai/design-system/components/ui/atoms/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import {
  OAUTH_PROVIDER_CATALOGUE,
  OAUTH2_PROVIDER,
} from "@arcadeai/design-system/metadata/oauth-providers";
import { Link } from "@tanstack/react-router";
import {
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Fingerprint,
  KeyRound,
  Loader2,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { usePlaygroundStore } from "@/app/playground/execute/hooks/use-store";
import type { Tool } from "@/app/tools/types";
import { analyzeToolRequirements } from "@/hooks/use-tool-requirements-status";

type RequirementsStatusProps = {
  tool: Tool;
  className?: string;
  onAuthorize?: () => void;
};

export function RequirementsStatus({
  tool,
  className,
  onAuthorize,
}: RequirementsStatusProps) {
  const authStatus = usePlaygroundStore((state) => state.authStatus);
  // Map store's "failed" status to "error" expected by analyzeToolRequirements
  const mappedAuthStatus =
    authStatus === "failed" ? "error" : (authStatus ?? undefined);
  const reqs = analyzeToolRequirements(tool, mappedAuthStatus);

  const [isOpen, setIsOpen] = useState(!reqs.allMet);

  if (!reqs.hasRequirements) {
    return null;
  }

  return (
    <Collapsible
      className={cn(
        "mb-2 rounded-lg border bg-muted/30",
        reqs.needsAction
          ? "border-amber-500/50 dark:border-amber-600/40"
          : "border-border/60",
        className
      )}
      onOpenChange={setIsOpen}
      open={isOpen}
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3">
          <h4 className="font-medium text-sm">Requirements</h4>
          <Badge className={reqs.badge.className} variant="secondary">
            {reqs.badge.label}
          </Badge>
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-3 px-4 pb-4">
          {reqs.hasOAuth && tool.requirements?.authorization ? (
            <OAuthRequirement
              authorization={tool.requirements.authorization}
              onAuthorize={onAuthorize}
            />
          ) : null}
          {reqs.hasSecrets && tool.requirements?.secrets ? (
            <SecretsRequirement secrets={tool.requirements.secrets} />
          ) : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

type OAuthRequirementProps = {
  authorization: NonNullable<
    NonNullable<Tool["requirements"]>["authorization"]
  >;
  onAuthorize?: () => void;
};

function OAuthRequirement({
  authorization,
  onAuthorize,
}: OAuthRequirementProps) {
  const authStatus = usePlaygroundStore((state) => state.authStatus);
  const isConfigured = authorization.status === "active";
  const isApproved = authorization.token_status === "completed";
  const hasScopes = (authorization.oauth2?.scopes?.length ?? 0) > 0;
  const providerId = authorization.provider_id ?? authorization.id ?? "";
  const providerDescriptor =
    OAUTH_PROVIDER_CATALOGUE[providerId] ?? OAUTH2_PROVIDER;
  const ProviderIcon = providerDescriptor.icon;

  const renderAuthStatus = () => {
    // Show authorizing state
    if (authStatus === "pending") {
      return (
        <span className="inline-flex items-center gap-1 text-amber-600 text-xs dark:text-amber-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Authorizing...
        </span>
      );
    }

    // Show success state (from store - will be cleared after query invalidation)
    if (authStatus === "completed") {
      return <StatusIndicator label="Authorized" met />;
    }

    // Show authorized from API data
    if (isApproved) {
      return <StatusIndicator label="Authorized" met />;
    }

    // Show connect button - softer styling since auth happens automatically on execute
    // Only render interactive button if onAuthorize callback is provided
    if (!onAuthorize) {
      return (
        <Tooltip>
          <TooltipTrigger
            render={
              <span className="inline-flex cursor-not-allowed items-center gap-1 text-muted-foreground text-xs" />
            }
          >
            Connect
            <ExternalLink className="h-3 w-3" />
          </TooltipTrigger>
          <TooltipContent>
            Authorization will be requested on first run
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              className="inline-flex cursor-pointer items-center gap-1 font-medium text-link text-xs underline underline-offset-2"
              onClick={onAuthorize}
              type="button"
            />
          }
        >
          Connect
          <ExternalLink className="h-3 w-3" />
        </TooltipTrigger>
        <TooltipContent>
          Click to connect now, or it will be requested on first run
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <div className="space-y-3 rounded-md border border-border/60 bg-background/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Fingerprint className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">OAuth Provider</span>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <StatusIndicator
            hint={
              isConfigured
                ? undefined
                : "Activate this provider in OAuth settings"
            }
            label={isConfigured ? "Configured" : "Not configured"}
            met={isConfigured}
          />
          {isConfigured ? renderAuthStatus() : null}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          className="inline-flex items-center gap-1.5 text-muted-foreground text-sm hover:text-foreground hover:underline"
          from="/orgs/$organizationId/projects/$projectId/auth/oauth"
          search={{ name: providerId }}
          to="."
        >
          <Badge className="gap-1.5" variant="secondary">
            <ProviderIcon className="h-3.5 w-3.5" />
            {providerId}
            <ExternalLink className="h-3 w-3" />
          </Badge>
        </Link>
        {hasScopes ? (
          <div className="text-muted-foreground text-xs">
            <span className="font-medium">Scopes: </span>
            {authorization.oauth2?.scopes?.join(", ")}
          </div>
        ) : null}
      </div>
    </div>
  );
}

type SecretsRequirementProps = {
  secrets: NonNullable<NonNullable<Tool["requirements"]>["secrets"]>;
};

function SecretsRequirement({ secrets }: SecretsRequirementProps) {
  const allMet = secrets.every((s) => s.met);
  const metCount = secrets.filter((s) => s.met).length;

  return (
    <div className="space-y-3 rounded-md border border-border/60 bg-background/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">API Keys & Secrets</span>
        </div>
        <StatusIndicator
          hint={allMet ? undefined : "Click a secret below to configure it"}
          label={
            allMet
              ? "All configured"
              : `${metCount}/${secrets.length} configured`
          }
          met={allMet}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {secrets.map((secret) => (
          <Tooltip key={secret.key}>
            <TooltipTrigger
              render={
                <Link
                  from="/orgs/$organizationId/projects/$projectId/auth/secrets"
                  search={
                    secret.met
                      ? { name: secret.key }
                      : { type: "new", newKey: secret.key }
                  }
                  to="."
                />
              }
            >
              <Badge
                className={cn(
                  "gap-1.5",
                  secret.met
                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
                    : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200"
                )}
                variant="secondary"
              >
                {secret.met ? (
                  <CheckCircle2 className="h-3 w-3" />
                ) : (
                  <XCircle className="h-3 w-3" />
                )}
                {secret.key}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {secret.met ? "Secret configured" : "Click to configure secret"}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}

type StatusIndicatorProps = {
  met: boolean;
  label: string;
  hint?: string;
};

function StatusIndicator({ met, label, hint }: StatusIndicatorProps) {
  const spanClassName = cn(
    "inline-flex items-center gap-1 text-xs",
    met
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-red-600 dark:text-red-400",
    hint ? "cursor-help" : "cursor-default"
  );

  const children = (
    <>
      {met ? (
        <CheckCircle2 className="h-3.5 w-3.5" />
      ) : (
        <XCircle className="h-3.5 w-3.5" />
      )}
      {label}
    </>
  );

  if (!hint) {
    return <span className={spanClassName}>{children}</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger render={<span className={spanClassName} />}>
        {children}
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}
